import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { SendingStatusResponse } from "../../shared/types";
import { api } from "../lib/api";

interface SendingValue {
  /** The workspace's live escalation status, or null until first load / on error. */
  status: SendingStatusResponse | null;
  /** Re-read `GET /api/sending/status` (call after a resume so the banner updates). */
  refresh: () => Promise<void>;
}

const SendingContext = createContext<SendingValue | null>(null);

/**
 * Owns the app-wide sending status (B3). Loaded once when the operator shell
 * mounts and refreshed after a resume, so the persistent escalation banner and
 * the Settings nav dot render on every screen without each surface refetching the
 * whole settings payload. A failed load leaves the status null — no banner shows,
 * which is the correct fail-safe.
 */
export function SendingProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<SendingStatusResponse | null>(null);

  const refresh = useCallback(async () => {
    try {
      setStatus(await api.sending.status());
    } catch {
      setStatus(null);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo<SendingValue>(() => ({ status, refresh }), [status, refresh]);
  return <SendingContext.Provider value={value}>{children}</SendingContext.Provider>;
}

export function useSending(): SendingValue {
  const ctx = useContext(SendingContext);
  if (!ctx) throw new Error("useSending must be used within SendingProvider");
  return ctx;
}

/**
 * The status where there may be no provider (a screen drawn on its own, as in
 * tests): null there, the same as not loaded yet, so nothing is claimed.
 */
export function useSendingStatus(): SendingStatusResponse | null {
  return useContext(SendingContext)?.status ?? null;
}
